import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Dialog, Popover, RadioGroup } from "@headlessui/react";

import { GetAssignmentDataByRole, submitReview } from "../api/assignments.api";
import { GetCandidateComments, CreateCandidateComment, DeleteComment } from "../api/candidates.api";
import { GetStatusForRole } from "../api/roles.api";
import { ReviewAssignment } from "../api/assignments.api";

import GitHubIcon from "@mui/icons-material/GitHub";
import LaunchIcon from "@mui/icons-material/Launch";
import {
  CheckBadgeIcon,
  EyeIcon,
  ChatBubbleLeftEllipsisIcon,
  StarIcon,
  PencilIcon,
  UserIcon,
  TrashIcon,
} from "@heroicons/react/20/solid";
import { v4 as uuidv4 } from "uuid";
import useApiToken from "../hooks/useApiToken";
import { CandidateComment, RoleStatus } from "../Models";
import useUserData from "../hooks/useUserData";
import ScoreGrades from "./Candidates/ScoreGrades";
import {
  AcademicCapIcon,
  GlobeAltIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { Spinner } from "./Shared/Spinner";
import { useNotification } from "../context/NotificationContext";

interface CandidateSubmission {
  firstName: string;
  lastName: string;
  assignmentLink: string;
  liveProjectLink: string;
  roleName: string;
  comments: string;
  candidateId: string;
  roleId: string;
  isReviewed: boolean;
  submissionDate: string;
}

interface PagedResult<T = any> {
  data: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

const Assignments: React.FC = () => {
  const { roleId } = useParams<{ roleId: string }>();
  const navigate = useNavigate();

  const [candidates, setCandidates] = useState<CandidateSubmission[]>([]);
  const [filteredCandidates, setFilteredCandidates] = useState<
    CandidateSubmission[]
  >([]);
  const [paged, setPaged] = useState<PagedResult<CandidateSubmission> | null>(
    null
  );
  const [filterReviewed, setFilterReviewed] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const pageSize = 20;
  const [isLoading, setIsLoading] = useState(false);
  const token = useApiToken();

  const [notes, setNotes] = useState<Record<string, CandidateComment[]>>({});
  const [noteInputs, setNoteInputs] = useState<Record<string, string>>({});
  const [selectedNoteStatus, setSelectedNoteStatus] = useState<
    Record<string, string>
  >({});
  const { success, error: notifyError } = useNotification();

  const [roleStatuses, setRoleStatuses] = useState<RoleStatus[]>([]);

  const { username, userId } = useUserData();

  useEffect(() => {
    const fetchData = async () => {
      if (!roleId || !token) return;

      try {
        setIsLoading(true);
        console.log("Fetching initial data for role:", roleId);

        const initialPaged: PagedResult<CandidateSubmission> =
          await GetAssignmentDataByRole(roleId, 1, "", pageSize, endDate || undefined);

        const data = initialPaged.data;

        const statusData = await GetStatusForRole(roleId, token);
        setRoleStatuses(
          statusData.roleStatuses.sort((a, b) => a.order - b.order)
        );

        setPaged(initialPaged);
        setCandidates(data);
        // apply current filter (reviewed) on current page
        const filtered = filterCandidates(data, filterReviewed, "");
        setFilteredCandidates(filtered);

        for (const c of data) {
          const fetchedNotes = await GetCandidateComments(c.candidateId, token);
          setNotes((prev) => ({ ...prev, [c.candidateId]: fetchedNotes }));
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        notifyError("Error", "Failed to load assignments. Please try again.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [roleId,endDate, token]);

  const filterCandidates = (
    list: CandidateSubmission[],
    reviewedFilter: string,
    _searchIgnored: string
  ) => {
    let filtered = list;
    if (reviewedFilter === "reviewed") {
      filtered = filtered.filter((c) => c.isReviewed);
    } else if (reviewedFilter === "not-reviewed") {
      filtered = filtered.filter((c) => !c.isReviewed);
    }
    return filtered;
  };

  const handleReviewedFilterChange = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const selectedReview = event.target.value;
    setFilterReviewed(selectedReview);
    const filtered = filterCandidates(candidates, selectedReview, "");
    setFilteredCandidates(filtered);
  };

  // debounce search
  useEffect(() => {
    if (searchQuery === "") return; // Don't search on empty query

    const id = setTimeout(() => {
      fetchPage(1, searchQuery);
    }, 300);
    return () => clearTimeout(id);
  }, [searchQuery]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const q = event.target.value;
    setSearchQuery(q);
  };

  const fetchPage = async (page: number, nameQuery: string = searchQuery) => {
    if (!roleId) return;
    try {
      setIsLoading(true);
      const res: PagedResult<CandidateSubmission> =
        await GetAssignmentDataByRole(roleId, page, nameQuery, pageSize, endDate || undefined);
      setPaged(res);
      setCandidates(res.data);
      setFilteredCandidates(filterCandidates(res.data, filterReviewed, ""));

      // fetch notes for visible candidates only
      for (const c of res.data) {
        const fetchedNotes = await GetCandidateComments(c.candidateId, token!);
        setNotes((prev) => ({ ...prev, [c.candidateId]: fetchedNotes }));
      }
    } catch (e) {
      // on error (e.g., 404), show empty results instead of stale data
      setPaged({
        data: [],
        page,
        pageSize,
        totalCount: 0,
        totalPages: 1,
        hasPreviousPage: false,
        hasNextPage: false,
      });
      setCandidates([]);
      setFilteredCandidates([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEndDateChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setEndDate(value);
  };

  const handleReviewToggle = async (
    candidateId: string,
    roleId: string,
    isReviewed: boolean
  ) => {
    if (!token) return;
    try {
      await ReviewAssignment(candidateId, roleId, isReviewed, token);

      const updatedCandidates = candidates.map((c) =>
        c.candidateId === candidateId ? { ...c, isReviewed } : c
      );
      setCandidates(updatedCandidates);

      const filtered = filterCandidates(
        updatedCandidates,
        filterReviewed,
        searchQuery
      );
      setFilteredCandidates(filtered);
      success("Success", `Candidate ${isReviewed ? "marked as reviewed" : "unmarked as reviewed"}!`);
    } catch (error) {
      console.error("Error updating review status:", error);
      notifyError("Error", "Failed to update review status. Please try again.");
    }
  };

  const handleCommentSubmit = async (
    candidateId: string,
    roleId: string,
    assignmentLink: string,
    comment: string
  ) => {
    if (!token) return;
    try {
      setIsLoading(true);

      await submitReview({
        candidateId,
        roleId,
        assignmentLink,
        comments: comment,
      }, token);

      const updatedCandidates = candidates.map((c) =>
        c.candidateId === candidateId ? { ...c, comments: comment } : c
      );
      setCandidates(updatedCandidates);
      const filtered = filterCandidates(
        updatedCandidates,
        filterReviewed,
        searchQuery
      );
      setFilteredCandidates(filtered);

      success("Success", "Public comment submitted successfully!");
    } catch (error) {
      console.error("Error handling assignment submission:", error);
      notifyError("Error", "Failed to submit comment. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleNameClick = (candidateId: string) => {
    navigate(`/roles/${roleId}/candidates/${candidateId}`);
  };

  const addNote = async (candidateId: string, roleId: string) => {
    const body = noteInputs[candidateId];
    const statusId = selectedNoteStatus[candidateId];
    if (!body?.trim() || !userId || !token || !statusId) return;

    const newNote: CandidateComment = {
      id: uuidv4(),
      roleId: roleId,
      candidateId: candidateId,
      comment: body,
      personId: userId,
      date: new Date().toISOString(),
      roleStatusId: statusId || undefined,
    };

    await CreateCandidateComment(newNote, token);

    setNotes((prev) => ({
      ...prev,
      [candidateId]: [...(prev[candidateId] || []), newNote],
    }));
    setNoteInputs((prev) => ({ ...prev, [candidateId]: "" }));
    setSelectedNoteStatus((prev) => ({ ...prev, [candidateId]: "" }));
  };

  const deleteNote = async (candidateId: string, noteId: string) => {
    if (!token) return;
    await DeleteComment(noteId, token);
    setNotes((prev) => ({
      ...prev,
      [candidateId]: prev[candidateId].filter((n) => n.id !== noteId),
    }));
  };

  const getLastNote = (candidateId: string) => {
    const candidateNotes = notes[candidateId] || [];
    if (candidateNotes.length === 0) return null;

    const sorted = candidateNotes.sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    return sorted[0];
  };

  const getStatusName = (statusId: string) => {
    return roleStatuses.find((rs) => rs.id === statusId)?.name || "";
  };

  const getSubmissionCounts = () => {
    const total = candidates.length;
    const reviewed = candidates.filter((c) => c.isReviewed).length;
    const notReviewed = total - reviewed;
    return { total, reviewed, notReviewed };
  };

  if (isLoading && candidates.length === 0) {
    return (
      <div className="flex justify-center items-center h-96">
        <Spinner height="h-10" width="w-10" />
      </div>
    );
  }

  const counts = getSubmissionCounts();
  const totalCount = paged?.totalCount ?? filteredCandidates.length;
  const totalPages = paged?.totalPages ?? 1;
  const safeCurrentPage = paged?.page ?? 1;
  const startIndex = totalCount === 0 ? 0 : (safeCurrentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + filteredCandidates.length, totalCount);
  const paginatedCandidates = filteredCandidates; // server already paginated

  const hasPreviousPage = paged?.hasPreviousPage ?? false;
  const hasNextPage = paged?.hasNextPage ?? false;

  let startPage = Math.max(1, safeCurrentPage - 2);
  let endPage = Math.min(totalPages, safeCurrentPage + 2);
  if (endPage - startPage < 4) {
    if (startPage === 1) endPage = Math.min(totalPages, startPage + 4);
    else startPage = Math.max(1, endPage - 4);
  }
  const pages: number[] = [];
  for (let i = startPage; i <= endPage; i++) pages.push(i);

  return (
    <div className="m-8">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Assignment Submissions
          </h1>
          <div className="mt-2 flex gap-4 text-sm text-gray-600">
            <span>
              Total:{" "}
              <span className="font-semibold text-gray-900">
                {counts.total}
              </span>
            </span>
            <span>
              Reviewed:{" "}
              <span className="font-semibold text-green-600">
                {counts.reviewed}
              </span>
            </span>
            <span>
              Not Reviewed:{" "}
              <span className="font-semibold text-red-600">
                {counts.notReviewed}
              </span>
            </span>
          </div>
        </div>
        <div className="flex items-center gap-10">
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search by name"
            className="px-3 py-1 border border-gray-300 rounded-md w-60 placeholder:text-sm"
          />

          <div>
            <label className="mr-3 font-medium text-gray-700 text-sm">
              Filter by:
            </label>
            <select
              className="px-3 py-1 border border-gray-300 rounded-md text-sm"
              value={filterReviewed}
              onChange={handleReviewedFilterChange}
            >
              <option value="all">All Candidates</option>
              <option value="reviewed">Reviewed</option>
              <option value="not-reviewed">Not Reviewed</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="mr-1 font-medium text-gray-700 text-sm">End date:</label>
            <input
              type="date"
              value={endDate}
              onChange={handleEndDateChange}
              className="px-3 py-1 border border-gray-300 rounded-md text-sm"
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border ">
        <div className="grid grid-cols-12 gap-2 px-6 py-3 bg-gray-50 border-b font-medium text-gray-700 text-sm">
          <div className="col-span-1">Reviewed</div>
          <div className="col-span-2">Name</div>
          <div className="col-span-1">Links</div>
          <div className="col-span-1">Date</div>
          <div className="col-span-5">Latest Note</div>
          <div className="col-span-2 justify-self-center">Actions</div>
        </div>

        {paginatedCandidates.map((candidate, index) => (
          <div
            key={candidate.candidateId}
            className={`grid grid-cols-12 gap-4 px-6 py-4 border-b hover:bg-gray-50 ${
              index % 2 === 0 ? "bg-white" : "bg-gray-25"
            }`}
          >
            <div className="col-span-1 flex items-center">
              <input
                type="checkbox"
                checked={candidate.isReviewed}
                onChange={(e) =>
                  handleReviewToggle(
                    candidate.candidateId,
                    candidate.roleId,
                    e.target.checked
                  )
                }
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              />
            </div>

            <div className="col-span-2 flex items-center">
              <button
                onClick={() => handleNameClick(candidate.candidateId)}
                className="text-sm font-medium text-blue-600 hover:underline truncate"
              >
                {candidate.firstName} {candidate.lastName}
              </button>
            </div>

            <div className="col-span-1 flex items-center gap-3">
              <a
                href={candidate.assignmentLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-gray-600 hover:text-gray-900"
                title="GitHub Repository"
              >
                <GitHubIcon className="h-4 w-4" />
              </a>
              {candidate.liveProjectLink ? (
                <a
                  href={candidate.liveProjectLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center text-blue-600 hover:text-blue-800"
                  title="Live Demo"
                >
                  <GlobeAltIcon className="h-5 w-5" />
                </a>
              ) : (
                <span className="text-gray-400 text-xs">No demo</span>
              )}
            </div>
            <div className="col-span-1 flex items-center">
              <span className="text-sm text-gray-500">
                {new Date(candidate.submissionDate + "Z").toLocaleDateString()}
              </span>
            </div>
            <div className="col-span-5 flex items-center">
              {(() => {
                const lastNote = getLastNote(candidate.candidateId);
                return lastNote ? (
                  <div className="flex items-center gap-2 w-full">
                    <p className="text-sm text-gray-900  flex-1">
                      {lastNote.comment}
                    </p>
                    {lastNote.roleStatusId && (
                      <span className="text-xs font-medium text-indigo-600 whitespace-nowrap">
                        {getStatusName(lastNote.roleStatusId)}
                      </span>
                    )}
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {new Date(lastNote.date).toLocaleDateString()}
                    </span>
                  </div>
                ) : (
                  <span className="text-sm text-gray-500">No notes yet</span>
                );
              })()}
            </div>

            <div className="col-span-2 flex items-center gap-1 justify-self-center">
              <Popover className="relative">
                <Popover.Button
                  className="p-2 rounded-md hover:bg-gray-100 text-blue-600"
                  title="View Score Grades"
                >
                  <AcademicCapIcon className="h-4 w-4" />
                </Popover.Button>
                <Popover.Panel className="absolute z-20 mt-2 w-96 right-0 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 p-4">
                  <div className="flex justify-between items-center mb-2">
                    <h3 className="text-sm font-semibold text-gray-700">
                      Score Grades
                    </h3>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    <ScoreGrades
                      candidateId={candidate.candidateId}
                      roleId={roleId}
                      hideScores={true}
                    />
                  </div>
                </Popover.Panel>
              </Popover>

              <Popover className="relative">
                <Popover.Button
                  className="p-2 rounded-md hover:bg-gray-100 text-blue-600"
                  title="Public Review"
                >
                  <ChatBubbleLeftEllipsisIcon className="h-4 w-4" />
                </Popover.Button>
                <Popover.Panel className="absolute z-20 mt-2 w-80 right-0 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 p-4">
                  <div className="mb-2">
                    <h3 className="text-sm font-semibold text-gray-700">
                      Public Review
                    </h3>
                  </div>
                  <textarea
                    value={candidate.comments}
                    onChange={(e) =>
                      setFilteredCandidates((prev) =>
                        prev.map((c) =>
                          c.candidateId === candidate.candidateId
                            ? { ...c, comments: e.target.value }
                            : c
                        )
                      )
                    }
                    className="w-full h-20 px-3 py-2 border border-gray-300 rounded-md text-sm"
                    placeholder="Write public review..."
                  />
                  <button
                    onClick={() =>
                      handleCommentSubmit(
                        candidate.candidateId,
                        candidate.roleId,
                        candidate.assignmentLink,
                        candidate.comments
                      )
                    }
                    className="mt-2 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  >
                    Save
                  </button>
                </Popover.Panel>
              </Popover>

              <Popover className="relative">
                <Popover.Button
                  className="p-2 rounded-md hover:bg-gray-100 text-yellow-600"
                  title="Internal Notes"
                >
                  <EyeIcon className="h-4 w-4" />
                </Popover.Button>
                <Popover.Panel className="absolute z-20 mt-2 w-96 right-0 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 p-4">
                  <div className="mb-2">
                    <h3 className="text-sm font-semibold text-gray-700">
                      Internal Notes
                    </h3>
                  </div>

                  <div className="space-y-2 max-h-48 overflow-y-auto mb-4">
                    {notes[candidate.candidateId]?.length ? (
                      notes[candidate.candidateId]
                        .sort(
                          (a, b) =>
                            new Date(b.date).getTime() -
                            new Date(a.date).getTime()
                        )
                        .map((note) => (
                          <div
                            key={note.id}
                            className="flex justify-between items-start text-sm bg-gray-50 p-2 rounded"
                          >
                            <div className="flex-1">
                              <span className="text-gray-900">
                                {note.comment}
                              </span>
                              {note.roleStatusId && (
                                <div className="text-xs text-indigo-600 mt-1">
                                  {getStatusName(note.roleStatusId)}
                                </div>
                              )}
                              <div className="text-xs text-gray-500 mt-1">
                                {new Date(note.date).toLocaleDateString()}
                              </div>
                            </div>
                            <TrashIcon
                              className="w-4 h-4 text-red-500 cursor-pointer ml-2"
                              onClick={() =>
                                deleteNote(candidate.candidateId, note.id)
                              }
                            />
                          </div>
                        ))
                    ) : (
                      <span className="text-sm text-gray-500">
                        No notes yet
                      </span>
                    )}
                  </div>

                  <div className="border-t pt-4">
                    <div className="mb-2">
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select
                        value={selectedNoteStatus[candidate.candidateId] || ""}
                        onChange={(e) =>
                          setSelectedNoteStatus((prev) => ({
                            ...prev,
                            [candidate.candidateId]: e.target.value,
                          }))
                        }
                        className="w-full px-2 py-1 border rounded text-sm"
                      >
                        <option value="">No Status</option>
                        {roleStatuses.map((status) => (
                          <option key={status.id} value={status.id}>
                            {status.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={noteInputs[candidate.candidateId] || ""}
                        onChange={(e) =>
                          setNoteInputs((prev) => ({
                            ...prev,
                            [candidate.candidateId]: e.target.value,
                          }))
                        }
                        placeholder="Add a note"
                        className="flex-1 px-2 py-1 border rounded text-sm"
                      />
                      <button
                        onClick={() =>
                          addNote(candidate.candidateId, candidate.roleId)
                        }
                        className="px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700"
                      >
                        Add
                      </button>
                    </div>
                  </div>
                </Popover.Panel>
              </Popover>
            </div>
          </div>
        ))}
      </div>

      {filteredCandidates.length === 0 && !isLoading && (
        <div className="text-center py-16 bg-white rounded-lg border">
          <UserIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No Candidates Found
          </h3>
          <p className="text-gray-500">
            No candidates match your current filter criteria.
          </p>
        </div>
      )}

      {filteredCandidates.length > 0 && (
        <div className="bg-white px-6 py-3 border-t border-gray-200 mt-2 flex items-center justify-between">
          <div className="text-sm text-gray-700">
            <span>
              Showing {totalCount === 0 ? 0 : startIndex + 1} to {endIndex} of{" "}
              {totalCount} results
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => fetchPage(1)}
              disabled={!hasPreviousPage}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              First
            </button>
            <button
              onClick={() => fetchPage(Math.max(1, safeCurrentPage - 1))}
              disabled={!hasPreviousPage}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            {pages.map((p) => (
              <button
                key={p}
                onClick={() => fetchPage(p)}
                className={`px-3 py-1 text-sm rounded ${
                  p === safeCurrentPage
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {p}
              </button>
            ))}

            <button
              onClick={() =>
                fetchPage(Math.min(totalPages, safeCurrentPage + 1))
              }
              disabled={!hasNextPage}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
            <button
              onClick={() => fetchPage(totalPages)}
              disabled={!hasNextPage}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Last
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Assignments;
